import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const API = '/api';

/* ── Inline SVG Icons ── */
const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg
        width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    >
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    plus: ['M12 5v14', 'M5 12h14'],
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
    x: ['M18 6 6 18', 'M6 6l12 12'],
    route: ['M9 18l6-6-6-6'],
    mapPin: ['M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z', 'M12 7a3 3 0 1 0 0 6 3 3 0 0 0 0-6z'],
    trip: ['M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z'],
    users: ['M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2', 'M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M23 21v-2a4 4 0 0 0-3-3.87', 'M16 3.13a4 4 0 0 1 0 7.75'],
    check: ['M20 6L9 17l-5-5'],
    alertTriangle: ['M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z', 'M12 9v4', 'M12 17h.01'],
    filter: ['M22 3H2l8 9.46V19l4 2v-8.54L22 3z'],
    fileText: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', 'M16 13H8', 'M16 17H8', 'M10 9H8'],
    bell: ['M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9', 'M13.73 21a2 2 0 0 1-3.46 0'],
    alertCircle: ['M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z', 'M12 8v4', 'M12 16h.01'],
};

const TABS = [
    { key: 'routes', label: 'Routes', icon: icons.route },
    { key: 'trips', label: 'Trips', icon: icons.trip },
    { key: 'passengers', label: 'Passengers', icon: icons.users },
    { key: 'allocation', label: 'Allocation', icon: icons.check },
    { key: 'reports', label: 'Reports', icon: icons.fileText },
];

export default function TripOperations({ user }) {
    const [activeTab, setActiveTab] = useState('routes');
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');

    // Data states
    const [routes, setRoutes] = useState([]);
    const [trips, setTrips] = useState([]);
    const [passengers, setPassengers] = useState([]);
    const [allocations, setAllocations] = useState([]);
    const [clients, setClients] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [zones, setZones] = useState([]);
    const [reportFilters, setReportFilters] = useState({ date: '', client: '', route: '', type: '', zone: '' });

    // Modal states
    const [modal, setModal] = useState(null); // { type: 'route'|'trip'|..., data: null|object }
    const [deleteModal, setDeleteModal] = useState(null);

    const operatorId = user?.operatorId || user?._id || 'all';
    const [notifications, setNotifications] = useState([]);
    const [showNotifPanel, setShowNotifPanel] = useState(false);

    const fetchNotifications = async () => {
        if (!operatorId || operatorId === 'all') return;
        try {
            const res = await axios.get(`${API}/notifications/operator/${operatorId}`);
            setNotifications(res.data);
        } catch (err) { console.error(err); }
    };

    useEffect(() => {
        fetchData();
        fetchCommonData();
        fetchNotifications();
    }, [activeTab]);

    const fetchCommonData = async () => {
        try {
            const [clientRes, zoneRes, vendorRes] = await Promise.all([
                axios.get(`${API}/clients/${operatorId}`),
                axios.get(`${API}/master-data`),
                axios.get(`${API}/vendors/${operatorId}`)
            ]);
            setClients(clientRes.data);
            setZones(zoneRes.data.filter(z => z.category === 'route-zone'));
            setVendors(vendorRes.data);
        } catch (err) { console.error(err); }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            if (activeTab === 'routes') {
                const res = await axios.get(`${API}/routes/${operatorId}`);
                setRoutes(res.data);
            } else if (activeTab === 'trips') {
                const res = await axios.get(`${API}/trips/${operatorId}`);
                setTrips(res.data);
            } else if (activeTab === 'passengers') {
                const res = await axios.get(`${API}/passengers/${operatorId}`);
                setPassengers(res.data);
            } else if (activeTab === 'allocation') {
                const res = await axios.get(`${API}/passenger-allocations/${operatorId}`);
                setAllocations(res.data);
            }
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    // --- RENDER HELPERS ---

    const renderRoutes = () => (
        <div className="md-table-wrap">
            <table className="md-table">
                <thead>
                    <tr>
                        <th className="md-th">Route Name</th>
                        <th className="md-th">Pickup Location</th>
                        <th className="md-th">Drop Location</th>
                        <th className="md-th">Zone</th>
                        <th className="md-th">Status</th>
                        <th className="md-th">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {routes.map(r => (
                        <tr key={r._id}>
                            <td className="md-td"><strong>{r.name}</strong></td>
                            <td className="md-td">{r.pickupLocation}</td>
                            <td className="md-td">{r.dropLocation}</td>
                            <td className="md-td">{r.zone?.name || '—'}</td>
                            <td className="md-td">
                                <span className={`md-status-badge ${r.status === 'Active' ? 'md-badge-active' : 'md-badge-inactive'}`}>
                                    {r.status}
                                </span>
                            </td>
                            <td className="md-td">
                                <div className="md-actions">
                                    <button className="md-icon-btn" onClick={() => setModal({ type: 'route', data: r })}><Icon d={icons.edit} size={14} /></button>
                                    <button className="md-icon-btn md-icon-btn-danger" onClick={() => setDeleteModal({ type: 'route', data: r })}><Icon d={icons.trash} size={14} /></button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );



    const renderTrips = () => (
        <div className="md-table-wrap">
            <table className="md-table">
                <thead>
                    <tr>
                        <th className="md-th">Client</th>
                        <th className="md-th">Route</th>
                        <th className="md-th">Type</th>
                        <th className="md-th">Frequency</th>
                        <th className="md-th">Schedule</th>
                        <th className="md-th">Vendor/Status</th>
                        <th className="md-th">Status</th>
                        <th className="md-th">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {trips.map(t => (
                        <tr key={t._id}>
                            <td className="md-td"><strong>{t.clientId?.companyName || '—'}</strong></td>
                            <td className="md-td">
                                <div>{t.routeId?.name}</div>
                            </td>
                            <td className="md-td">
                                <span style={{ fontWeight: 600, color: t.tripType === 'Login' ? '#22c55e' : '#f59e0b' }}>{t.tripType}</span>
                            </td>
                            <td className="md-td">{t.tripFrequency}</td>
                            <td className="md-td">
                                {t.tripFrequency === 'One Time' ? (
                                    t.tripDate ? new Date(t.tripDate).toLocaleDateString() : '—'
                                ) : (
                                    t.recurringDays?.join(', ') || '—'
                                )}
                            </td>
                            <td className="md-td">
                                {t.vendorId ? (
                                    <div>
                                        <div style={{ fontWeight: 700, color: '#1e1b4b' }}>{t.vendorId.vendorName}</div>
                                        <div style={{ fontSize: 11, color: '#64748b' }}>
                                            Status: <span style={{
                                                color: t.vendorStatus === 'Accepted' ? '#16a34a' :
                                                    t.vendorStatus === 'Vendor Proposed' ? '#2563eb' : '#f59e0b',
                                                fontWeight: 800
                                            }}>{t.vendorStatus}</span>
                                        </div>
                                        {t.vendorStatus === 'Vendor Proposed' && (
                                            <div style={{ marginTop: 8, padding: 8, background: '#eff6ff', borderRadius: 8, border: '1px solid #bfdbfe' }}>
                                                <div style={{ fontSize: 10, fontWeight: 700, color: '#1d4ed8', marginBottom: 4 }}>VENDOR PROPOSAL:</div>
                                                <div style={{ fontSize: 11 }}>Vehicle: <strong>{t.vendorProposedVehicleId?.registrationNumber || '—'}</strong></div>
                                                <div style={{ fontSize: 11 }}>Driver: <strong>{t.vendorProposedDriverId?.name || '—'}</strong></div>
                                                <div style={{ fontSize: 11 }}>Amount: <strong>{t.vendorProposedAmount}</strong></div>
                                                <button
                                                    onClick={() => acceptVendorProposal(t)}
                                                    style={{
                                                        marginTop: 6, width: '100%', padding: '4px', background: '#16a34a', color: 'white',
                                                        border: 'none', borderRadius: 4, fontSize: 10, fontWeight: 700, cursor: 'pointer'
                                                    }}
                                                >
                                                    ACCEPT PROPOSAL
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ) : 'In-house'}
                            </td>
                            <td className="md-td">
                                <span className="md-status-badge md-badge-active">{t.status}</span>
                            </td>
                            <td className="md-td">
                                <div className="md-actions">
                                    <button className="md-icon-btn" onClick={() => setModal({ type: 'trip', data: t })}><Icon d={icons.edit} size={14} /></button>
                                    <button className="md-icon-btn md-icon-btn-danger" onClick={() => setDeleteModal({ type: 'trip', data: t })}><Icon d={icons.trash} size={14} /></button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    const renderPassengers = () => (
        <div className="md-table-wrap">
            <table className="md-table">
                <thead>
                    <tr>
                        <th className="md-th">Passenger Name</th>
                        <th className="md-th">Employee ID</th>
                        <th className="md-th">Client</th>
                        <th className="md-th">Contact</th>
                        <th className="md-th">Locations</th>
                        <th className="md-th">Status</th>
                        <th className="md-th">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {passengers.map(p => (
                        <tr key={p._id}>
                            <td className="md-td"><strong>{p.name}</strong></td>
                            <td className="md-td">{p.employeeId}</td>
                            <td className="md-td">{p.clientId?.companyName || '—'}</td>
                            <td className="md-td">{p.contactNumber || p.email}</td>
                            <td className="md-td">
                                <div style={{ fontSize: 11 }}>H: {p.pickupLocation || '—'}</div>
                                <div style={{ fontSize: 11 }}>W: {p.dropLocation || '—'}</div>
                            </td>
                            <td className="md-td">
                                <span className={`md-status-badge ${p.status === 'Active' ? 'md-badge-active' : 'md-badge-inactive'}`}>
                                    {p.status}
                                </span>
                            </td>
                            <td className="md-td">
                                <div className="md-actions">
                                    <button className="md-icon-btn" onClick={() => setModal({ type: 'passenger', data: p })}><Icon d={icons.edit} size={14} /></button>
                                    <button className="md-icon-btn md-icon-btn-danger" onClick={() => setDeleteModal({ type: 'passenger', data: p })}><Icon d={icons.trash} size={14} /></button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    const renderAllocations = () => (
        <div className="md-table-wrap">
            <table className="md-table">
                <thead>
                    <tr>
                        <th className="md-th">Passenger</th>
                        <th className="md-th">Client</th>
                        <th className="md-th">Trip Detail</th>
                        <th className="md-th">Type</th>
                        <th className="md-th">Pickup/Drop Points</th>
                        <th className="md-th">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {allocations.map(a => (
                        <tr key={a._id}>
                            <td className="md-td"><strong>{a.passengerId?.name}</strong></td>
                            <td className="md-td">{a.tripId?.clientId?.companyName}</td>
                            <td className="md-td">
                                <div>{a.tripId?.routeId?.name} ({a.tripId?.tripType})</div>
                                <div style={{ fontSize: 11, color: '#64748b' }}>{a.tripId?.tripFrequency}</div>
                            </td>
                            <td className="md-td">{a.allocationType}</td>
                            <td className="md-td">
                                <div>P: {a.pickupPoint || 'Default'}</div>
                                <div>D: {a.dropPoint || 'Default'}</div>
                            </td>
                            <td className="md-td">
                                <button className="md-icon-btn md-icon-btn-danger" onClick={() => setDeleteModal({ type: 'allocation', data: a })}><Icon d={icons.trash} size={14} /></button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );


    const renderReports = () => {
        const filteredTrips = trips.filter(t => {
            if (reportFilters.client && t.clientId?._id !== reportFilters.client) return false;
            if (reportFilters.route && t.routeId?._id !== reportFilters.route) return false;
            if (reportFilters.type && t.tripType !== reportFilters.type) return false;
            if (reportFilters.zone) {
                const route = routes.find(r => r._id === t.routeId?._id);
                if (route?.zone?._id !== reportFilters.zone) return false;
            }
            if (reportFilters.date && t.tripDate) {
                if (t.tripDate.split('T')[0] !== reportFilters.date) return false;
            }
            return true;
        });

        return (
            <div style={{ padding: '20px' }}>
                <div className="report-filters" style={{ display: 'flex', gap: 15, marginBottom: 25, flexWrap: 'wrap', padding: '20px', background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                    <div className="md-form-group" style={{ margin: 0, minWidth: 160 }}>
                        <label className="md-label" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Date</label>
                        <input type="date" className="md-input" value={reportFilters.date} onChange={e => setReportFilters({ ...reportFilters, date: e.target.value })} style={{ height: 38 }} />
                    </div>
                    <div className="md-form-group" style={{ margin: 0, minWidth: 160 }}>
                        <label className="md-label" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Client</label>
                        <select className="md-input" value={reportFilters.client} onChange={e => setReportFilters({ ...reportFilters, client: e.target.value })} style={{ height: 38 }}>
                            <option value="">All Clients</option>
                            {clients.map(c => <option key={c._id} value={c._id}>{c.companyName}</option>)}
                        </select>
                    </div>
                    <div className="md-form-group" style={{ margin: 0, minWidth: 160 }}>
                        <label className="md-label" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Route</label>
                        <select className="md-input" value={reportFilters.route} onChange={e => setReportFilters({ ...reportFilters, route: e.target.value })} style={{ height: 38 }}>
                            <option value="">All Routes</option>
                            {routes.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
                        </select>
                    </div>
                    <div className="md-form-group" style={{ margin: 0, minWidth: 160 }}>
                        <label className="md-label" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Zone</label>
                        <select className="md-input" value={reportFilters.zone} onChange={e => setReportFilters({ ...reportFilters, zone: e.target.value })} style={{ height: 38 }}>
                            <option value="">All Zones</option>
                            {zones.map(z => <option key={z._id} value={z._id}>{z.name}</option>)}
                        </select>
                    </div>
                    <div className="md-form-group" style={{ margin: 0, minWidth: 140 }}>
                        <label className="md-label" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Type</label>
                        <select className="md-input" value={reportFilters.type} onChange={e => setReportFilters({ ...reportFilters, type: e.target.value })} style={{ height: 38 }}>
                            <option value="">All Types</option>
                            <option value="Login">Login</option>
                            <option value="Logout">Logout</option>
                        </select>
                    </div>
                </div>

                <div className="md-table-wrap">
                    <table className="md-table">
                        <thead>
                            <tr>
                                <th className="md-th">Trip Details</th>
                                <th className="md-th">Client</th>
                                <th className="md-th">Schedule</th>
                                <th className="md-th">Passengers</th>
                                <th className="md-th">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredTrips.map(t => {
                                const tripAllocations = allocations.filter(a => a.tripId?._id === t._id);
                                return (
                                    <tr key={t._id}>
                                        <td className="md-td">
                                            <div style={{ fontWeight: 700, color: '#1e1b4b' }}>{t.routeId?.name}</div>
                                            <div style={{ fontSize: 12, color: t.tripType === 'Login' ? '#16a34a' : '#ea580c' }}>
                                                {t.tripType.toUpperCase()}
                                            </div>
                                        </td>
                                        <td className="md-td">{t.clientId?.companyName}</td>
                                        <td className="md-td">
                                            <div>{t.tripFrequency}</div>
                                            <div style={{ fontSize: 11, color: '#64748b' }}>
                                                {t.tripFrequency === 'One Time' ? (t.tripDate ? new Date(t.tripDate).toLocaleDateString() : '—') : t.recurringDays?.join(', ')}
                                            </div>
                                        </td>
                                        <td className="md-td">
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                <div style={{ fontWeight: 700, fontSize: 15 }}>{tripAllocations.length} Passengers</div>
                                                <div style={{ fontSize: 11, color: '#64748b', maxWidth: '200px' }}>
                                                    {tripAllocations.map(a => a.passengerId?.name).join(', ') || 'No passengers allocated'}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="md-td">
                                            <span className="md-status-badge md-badge-active">{t.status}</span>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // --- MODAL SUB-COMPONENTS ---

    const RouteModal = ({ data, onClose, onSave }) => {
        const [form, setForm] = useState(data || { name: '', pickupLocation: '', dropLocation: '', zone: '', description: '', status: 'Active' });
        return (
            <div className="md-modal-overlay" onClick={onClose}>
                <div className="md-modal" onClick={e => e.stopPropagation()}>
                    <div className="md-modal-header">
                        <h3 className="md-modal-title">{data ? 'Edit Route' : 'Add New Route'}</h3>
                        <button className="md-modal-close" onClick={onClose}><Icon d={icons.x} size={18} /></button>
                    </div>
                    <div className="md-modal-body">
                        <div className="md-form-group">
                            <label className="md-label">Route Name*</label>
                            <input className="md-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. IT Park Shuttle" />
                        </div>
                        <div className="md-form-group">
                            <label className="md-label">Pickup Location (Google Search)*</label>
                            <input className="md-input" value={form.pickupLocation} onChange={e => setForm({ ...form, pickupLocation: e.target.value })} placeholder="Search pickup..." />
                        </div>
                        <div className="md-form-group">
                            <label className="md-label">Drop Location (Google Search)*</label>
                            <input className="md-input" value={form.dropLocation} onChange={e => setForm({ ...form, dropLocation: e.target.value })} placeholder="Search drop..." />
                        </div>
                        <div className="md-form-group">
                            <label className="md-label">Zone</label>
                            <select className="md-input" value={form.zone} onChange={e => setForm({ ...form, zone: e.target.value })}>
                                <option value="">Select Zone</option>
                                {zones.map(z => <option key={z._id} value={z._id}>{z.name}</option>)}
                            </select>
                        </div>
                        <div className="md-form-group">
                            <label className="md-label">Description</label>
                            <textarea className="md-textarea" rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                        </div>
                        <div className="md-form-group">
                            <label className="md-label">Status</label>
                            <select className="md-input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                            </select>
                        </div>
                    </div>
                    <div className="md-modal-footer">
                        <button className="md-cancel-btn" onClick={onClose}>Cancel</button>
                        <button className="md-save-btn" onClick={() => onSave(form)}>Save Route</button>
                    </div>
                </div>
            </div>
        );
    };



    const TripModal = ({ data, onClose, onSave }) => {
        const [form, setForm] = useState(data || {
            clientId: '', routeId: '', pickupLocation: '', dropLocation: '',
            tripType: 'Login', tripFrequency: 'One Time', tripDate: '',
            recurringDays: [], status: 'Scheduled', vendorId: '', vendorStatus: 'Not Assigned'
        });

        const toggleDay = (day) => {
            const days = form.recurringDays.includes(day)
                ? form.recurringDays.filter(d => d !== day)
                : [...form.recurringDays, day];
            setForm({ ...form, recurringDays: days });
        };

        return (
            <div className="md-modal-overlay" onClick={onClose}>
                <div className="md-modal" style={{ maxWidth: 600 }} onClick={e => e.stopPropagation()}>
                    <div className="md-modal-header">
                        <h3 className="md-modal-title">{data ? 'Edit Trip' : 'Create Trip'}</h3>
                        <button className="md-modal-close" onClick={onClose}><Icon d={icons.x} size={18} /></button>
                    </div>
                    <div className="md-modal-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        <div className="md-form-group">
                            <label className="md-label">Client*</label>
                            <select className="md-input" value={form.clientId} onChange={e => setForm({ ...form, clientId: e.target.value })}>
                                <option value="">Select Client</option>
                                {clients.map(c => <option key={c._id} value={c._id}>{c.companyName}</option>)}
                            </select>
                        </div>
                        <div className="md-form-group">
                            <label className="md-label">Route*</label>
                            <select className="md-input" value={form.routeId} onChange={e => setForm({ ...form, routeId: e.target.value })}>
                                <option value="">Select Route</option>
                                {routes.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
                            </select>
                        </div>
                        <div className="md-form-group">
                            <label className="md-label">Assign Vendor</label>
                            <select className="md-input" value={form.vendorId?._id || form.vendorId} onChange={e => {
                                const vId = e.target.value;
                                setForm({
                                    ...form,
                                    vendorId: vId,
                                    vendorStatus: vId ? 'Pending Vendor' : 'Not Assigned'
                                });
                            }}>
                                <option value="">In-house (No Vendor)</option>
                                {vendors.map(v => <option key={v._id} value={v._id}>{v.vendorName}</option>)}
                            </select>
                        </div>

                        <div className="md-form-group">
                            <label className="md-label">Trip Type*</label>
                            <select className="md-input" value={form.tripType} onChange={e => setForm({ ...form, tripType: e.target.value })}>
                                <option value="Login">Login (Pickup)</option>
                                <option value="Logout">Logout (Drop)</option>
                            </select>
                        </div>
                        <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                            <label className="md-label">Pickup Location*</label>
                            <input className="md-input" value={form.pickupLocation} onChange={e => setForm({ ...form, pickupLocation: e.target.value })} />
                        </div>
                        <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                            <label className="md-label">Drop Location*</label>
                            <input className="md-input" value={form.dropLocation} onChange={e => setForm({ ...form, dropLocation: e.target.value })} />
                        </div>
                        <div className="md-form-group">
                            <label className="md-label">Frequency*</label>
                            <select className="md-input" value={form.tripFrequency} onChange={e => setForm({ ...form, tripFrequency: e.target.value })}>
                                <option value="One Time">One Time</option>
                                <option value="Recurring">Recurring</option>
                            </select>
                        </div>
                        {form.tripFrequency === 'One Time' ? (
                            <div className="md-form-group">
                                <label className="md-label">Trip Date*</label>
                                <input type="date" className="md-input" value={form.tripDate ? form.tripDate.split('T')[0] : ''} onChange={e => setForm({ ...form, tripDate: e.target.value })} />
                            </div>
                        ) : (
                            <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                <label className="md-label">Recurring Days</label>
                                <div style={{ display: 'flex', gap: 5 }}>
                                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                                        <button
                                            key={day}
                                            type="button"
                                            onClick={() => toggleDay(day)}
                                            style={{
                                                padding: '5px 8px', borderRadius: 6, border: '1px solid #e2e8f0',
                                                background: form.recurringDays.includes(day) ? '#1e1b4b' : '#fff',
                                                color: form.recurringDays.includes(day) ? '#fff' : '#64748b',
                                                fontSize: 11, fontWeight: 600, cursor: 'pointer'
                                            }}
                                        >
                                            {day}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="md-modal-footer">
                        <button className="md-cancel-btn" onClick={onClose}>Cancel</button>
                        <button className="md-save-btn" onClick={() => onSave(form)}>Save Trip</button>
                    </div>
                </div>
            </div>
        );
    };

    const PassengerModal = ({ data, onClose, onSave }) => {
        const [form, setForm] = useState(() => {
            if (data) {
                return {
                    ...data,
                    firstName: data.firstName || data.name?.split(' ')[0] || '',
                    lastName: data.lastName || data.name?.split(' ').slice(1).join(' ') || '',
                    email: data.email || '',
                    contactNumber: data.contactNumber || '',
                    clientId: data.clientId?._id || data.clientId || '',
                    pickupLocation: data.pickupLocation || '',
                    dropLocation: data.dropLocation || '',
                    status: data.status || 'Active'
                };
            }
            return {
                firstName: '', lastName: '', name: '', email: '', employeeId: '', contactNumber: '', clientId: '',
                pickupLocation: '', dropLocation: '', status: 'Active'
            };
        });

        const handleLocalSave = () => {
            if (!form.firstName || !form.lastName || !form.email || !form.clientId) {
                alert("Please fill in all required fields (First Name, Last Name, Email, Client)");
                return;
            }
            const finalData = {
                ...form,
                name: `${form.firstName} ${form.lastName}`.trim()
            };
            onSave(finalData);
        };

        return (
            <div className="md-modal-overlay" onClick={onClose}>
                <div className="md-modal" style={{ maxWidth: 600 }} onClick={e => e.stopPropagation()}>
                    <div className="md-modal-header">
                        <h3 className="md-modal-title">{data ? 'Update Passenger Record' : 'Register New Passenger'}</h3>
                        <button className="md-modal-close" onClick={onClose}><Icon d={icons.x} size={18} /></button>
                    </div>
                    <div className="md-modal-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <div className="md-form-group">
                            <label className="md-label">First Name*</label>
                            <input className="md-input" value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} placeholder="John" />
                        </div>
                        <div className="md-form-group">
                            <label className="md-label">Last Name*</label>
                            <input className="md-input" value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} placeholder="Doe" />
                        </div>
                        <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                            <label className="md-label">Email Address* (for Synchronization)</label>
                            <input className="md-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="passenger@company.com" />
                        </div>
                        <div className="md-form-group">
                            <label className="md-label">Contact Number*</label>
                            <input className="md-input" value={form.contactNumber} onChange={e => setForm({ ...form, contactNumber: e.target.value })} placeholder="Primary Phone" />
                        </div>
                        <div className="md-form-group">
                            <label className="md-label">Employee ID / Passenger ID</label>
                            <input className="md-input" value={form.employeeId} onChange={e => setForm({ ...form, employeeId: e.target.value })} placeholder="EMP-123" />
                        </div>
                        <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                            <label className="md-label">Client Association*</label>
                            <select className="md-input" value={form.clientId} onChange={e => setForm({ ...form, clientId: e.target.value })}>
                                <option value="">Select a Client</option>
                                {clients.map(c => <option key={c._id} value={c._id}>{c.companyName}</option>)}
                            </select>
                        </div>
                        <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                            <label className="md-label">Default Pickup / Home Location</label>
                            <input className="md-input" value={form.pickupLocation} onChange={e => setForm({ ...form, pickupLocation: e.target.value })} placeholder="Address..." />
                        </div>
                        <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                            <label className="md-label">Default Drop / Work Location</label>
                            <input className="md-input" value={form.dropLocation} onChange={e => setForm({ ...form, dropLocation: e.target.value })} placeholder="Address..." />
                        </div>
                    </div>
                    <div className="md-modal-footer">
                        <button className="md-cancel-btn" onClick={onClose}>Discard</button>
                        <button className="md-save-btn" onClick={handleLocalSave}>Save Passenger</button>
                    </div>
                </div>
            </div>
        );
    };

    const AllocationModal = ({ onClose, onSave }) => {
        const [form, setForm] = useState({ passengerId: '', tripId: '', allocationType: 'One Time', pickupPoint: '', dropPoint: '' });

        const filteredPassengers = passengers.filter(p => {
            const trip = trips.find(t => t._id === form.tripId);
            if (!trip) return true;
            return p.clientId?._id === trip.clientId?._id || p.clientId === trip.clientId;
        });

        return (
            <div className="md-modal-overlay" onClick={onClose}>
                <div className="md-modal" onClick={e => e.stopPropagation()}>
                    <div className="md-modal-header">
                        <h3 className="md-modal-title">Allocate Passenger to Trip</h3>
                        <button className="md-modal-close" onClick={onClose}><Icon d={icons.x} size={18} /></button>
                    </div>
                    <div className="md-modal-body">
                        <div className="md-form-group">
                            <label className="md-label">Trip*</label>
                            <select className="md-input" value={form.tripId} onChange={e => setForm({ ...form, tripId: e.target.value })}>
                                <option value="">Select Trip</option>
                                {trips.map(t => <option key={t._id} value={t._id}>{t.routeId?.name} - {t.tripType} ({t.clientId?.companyName})</option>)}
                            </select>
                        </div>
                        <div className="md-form-group">
                            <label className="md-label">Passenger*</label>
                            <select className="md-input" value={form.passengerId} onChange={e => setForm({ ...form, passengerId: e.target.value })}>
                                <option value="">Select Passenger</option>
                                {filteredPassengers.map(p => <option key={p._id} value={p._id}>{p.name} ({p.employeeId})</option>)}
                            </select>
                        </div>
                        <div className="md-form-group">
                            <label className="md-label">Allocation Type</label>
                            <select className="md-input" value={form.allocationType} onChange={e => setForm({ ...form, allocationType: e.target.value })}>
                                <option value="One Time">One Time</option>
                                <option value="Recurring">Recurring</option>
                            </select>
                        </div>
                        <div className="md-form-group">
                            <label className="md-label">Specific Pickup Point (Optional)</label>
                            <input className="md-input" value={form.pickupPoint} onChange={e => setForm({ ...form, pickupPoint: e.target.value })} placeholder="Overrides route default" />
                        </div>
                        <div className="md-form-group">
                            <label className="md-label">Specific Drop Point (Optional)</label>
                            <input className="md-input" value={form.dropPoint} onChange={e => setForm({ ...form, dropPoint: e.target.value })} placeholder="Overrides route default" />
                        </div>
                    </div>
                    <div className="md-modal-footer">
                        <button className="md-cancel-btn" onClick={onClose}>Cancel</button>
                        <button className="md-save-btn" onClick={() => onSave(form)}>Allocate</button>
                    </div>
                </div>
            </div>
        );
    };

    const acceptVendorProposal = async (trip) => {
        try {
            await axios.put(`${API}/trips/${trip._id}`, {
                vehicleId: trip.vendorProposedVehicleId?._id || trip.vendorProposedVehicleId,
                driverId: trip.vendorProposedDriverId?._id || trip.vendorProposedDriverId,
                amount: trip.vendorProposedAmount,
                vendorStatus: 'Accepted'
            });
            fetchData();
        } catch (err) { console.error(err); }
    };

    // --- ACTIONS ---

    const handleSave = async (formData) => {
        const type = modal.type;
        const id = modal.data?._id;
        try {
            const payload = { ...formData, operatorId };
            let endpoint = '';
            if (type === 'route') endpoint = '/routes';
            else if (type === 'trip') endpoint = '/trips';
            else if (type === 'passenger') endpoint = '/passengers';
            else if (type === 'allocation') endpoint = '/passenger-allocations';

            if (id) {
                await axios.put(`${API}${endpoint}/${id}`, payload);
            } else {
                await axios.post(`${API}${endpoint}`, payload);
            }
            setModal(null);
            fetchData();
        } catch (err) { console.error(err); }
    };

    const handleDelete = async () => {
        const { type, data } = deleteModal;
        try {
            let endpoint = '';
            if (type === 'route') endpoint = '/routes';
            else if (type === 'trip') endpoint = '/trips';
            else if (type === 'passenger') endpoint = '/passengers';
            else if (type === 'allocation') endpoint = '/passenger-allocations';

            await axios.delete(`${API}${endpoint}/${data._id}`);
            setDeleteModal(null);
            fetchData();
        } catch (err) { console.error(err); }
    };

    return (
        <div className="md-page">
            <style>{`
                .md-page { padding: 30px; background: #f8fafc; min-height: 100vh; }
                .md-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; }
                .md-title { font-size: 28px; font-weight: 800; color: #1e293b; margin: 0; }
                .md-subtitle { font-size: 14px; color: #64748b; margin: 4px 0 0 0; }
                .md-panel { background: white; border-radius: 16px; border: 1px solid #e2e8f0; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); overflow: hidden; }
                .md-tabs-wrap { margin-bottom: 24px; border-bottom: 1px solid #e2e8f0; }
                .md-tabs { display: flex; gap: 30px; }
                .md-tab { background: none; border: none; padding: 12px 0; font-size: 14px; font-weight: 600; color: #64748b; cursor: pointer; display: flex; align-items: center; gap: 8px; position: relative; }
                .md-tab-active { color: #1e1b4b; }
                .md-tab-active::after { content: ''; position: absolute; bottom: -1px; left: 0; right: 0; height: 2px; background: #1e1b4b; }
                .md-panel-toolbar { padding: 20px 24px; background: #fafbfc; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
                .md-table { width: 100%; border-collapse: collapse; }
                .md-th { text-align: left; padding: 12px 24px; font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; background: #f8fafc; border-bottom: 1px solid #e2e8f0; }
                .md-td { padding: 16px 24px; border-bottom: 1px solid #f1f5f9; font-size: 14px; color: #334155; }
                .md-actions { display: flex; gap: 8px; }
                .md-icon-btn { width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; border: 1px solid #e2e8f0; border-radius: 8px; background: white; cursor: pointer; color: #64748b; transition: all 0.2s; }
                .md-icon-btn:hover { border-color: #1e1b4b; color: #1e1b4b; }
                .md-icon-btn-danger:hover { border-color: #ef4444; color: #ef4444; }
                .md-add-btn { background: #1e1b4b; color: white; border: none; padding: 10px 20px; border-radius: 10px; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 8px; font-size: 13px; transition: all 0.2s; }
                .md-add-btn:hover { background: #2d2a6b; transform: translateY(-1px); }
                .md-badge-active { background: #dcfce7; color: #166534; }
                .md-badge-inactive { background: #fee2e2; color: #991b1b; }
                .md-status-badge { padding: 4px 12px; border-radius: 99px; font-size: 11px; font-weight: 700; }
                .md-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
                .md-modal { background: white; border-radius: 20px; width: 90%; max-width: 500px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.2); }
                .md-modal-header { padding: 24px; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; }
                .md-modal-title { font-size: 18px; font-weight: 700; margin: 0; }
                .md-modal-body { padding: 24px; }
                .md-form-group { margin-bottom: 16px; }
                .md-label { display: block; font-size: 13px; font-weight: 600; color: #475569; margin-bottom: 6px; }
                .md-input { width: 100%; height: 44px; padding: 0 16px; border: 1.5px solid #e2e8f0; border-radius: 10px; outline: none; transition: border-color 0.2s; font-size: 14px; box-sizing: border-box; }
                .md-input:focus { border-color: #1e1b4b; }
                .md-textarea { width: 100%; border: 1.5px solid #e2e8f0; border-radius: 10px; padding: 12px 16px; outline: none; font-size: 14px; box-sizing: border-box; }
                .md-modal-footer { padding: 20px 24px; border-top: 1px solid #f1f5f9; display: flex; justify-content: flex-end; gap: 12px; }
                .md-cancel-btn { background: white; border: 1.5px solid #e2e8f0; padding: 10px 20px; border-radius: 10px; font-weight: 600; cursor: pointer; color: #64748b; }
                .md-save-btn { background: #1e1b4b; color: white; border: none; padding: 10px 24px; border-radius: 10px; font-weight: 700; cursor: pointer; }
                .md-search-wrap { position: relative; display: flex; align-items: center; }
                .md-search-wrap svg { position: absolute; left: 12px; color: #94a3b8; }
                .md-search { padding-left: 36px; width: 250px; }
            `}</style>

            <div className="md-header">
                <div>
                    <h1 className="md-title">Trip Operations</h1>
                    <p className="md-subtitle">Manage routes, trips, passengers and operational reports.</p>
                </div>
                {/* Operator Notification Bell */}
                <div style={{ position: 'relative' }}>
                    <button
                        onClick={() => setShowNotifPanel(p => !p)}
                        style={{ cursor: 'pointer', padding: 8, position: 'relative', display: 'flex', alignItems: 'center', borderRadius: 10, background: notifications.some(n => !n.isRead) ? '#fef9c3' : '#f8fafc', border: `1px solid ${notifications.some(n => !n.isRead) ? '#fcd34d' : '#e2e8f0'}` }}
                        title="Vendor Notifications"
                    >
                        <Icon d={icons.bell} size={18} />
                        {notifications.filter(n => !n.isRead).length > 0 && (
                            <span style={{ position: 'absolute', top: 4, right: 4, background: '#ef4444', color: 'white', borderRadius: '50%', width: 14, height: 14, fontSize: 8, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {notifications.filter(n => !n.isRead).length}
                            </span>
                        )}
                    </button>
                    {showNotifPanel && (
                        <div style={{ position: 'absolute', top: 48, right: 0, width: 340, background: 'white', borderRadius: 14, boxShadow: '0 20px 40px rgba(0,0,0,0.15)', border: '1px solid #e5e7eb', zIndex: 999 }} onClick={e => e.stopPropagation()}>
                            <div style={{ padding: '14px 16px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontWeight: 800, fontSize: 14, color: '#111827' }}>Vendor Notifications</span>
                                {notifications.some(n => !n.isRead) && (
                                    <button onClick={async () => {
                                        await axios.put(`${API}/notifications/mark-all-read/operator/${operatorId}`);
                                        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
                                    }} style={{ fontSize: 11, color: '#10b981', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>Mark all read</button>
                                )}
                            </div>
                            <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                                {notifications.length === 0 ? (
                                    <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>No vendor notifications yet</div>
                                ) : notifications.map(n => (
                                    <div key={n._id} style={{ padding: '12px 16px', borderBottom: '1px solid #f9fafb', background: n.isRead ? 'white' : '#fefce8', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: n.type === 'vendor_responded' ? '#3b82f6' : '#10b981', marginTop: 5, flexShrink: 0 }} />
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>{n.title}</div>
                                            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2, lineHeight: 1.4 }}>{n.message}</div>
                                            <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>{new Date(n.createdAt).toLocaleString()}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="md-tabs-wrap">
                <nav className="md-tabs">
                    {TABS.map(t => (
                        <button
                            key={t.key}
                            className={`md-tab ${activeTab === t.key ? 'md-tab-active' : ''}`}
                            onClick={() => setActiveTab(t.key)}
                        >
                            <Icon d={t.icon} size={15} />
                            {t.label}
                        </button>
                    ))}
                </nav>
            </div>

            <div className="md-panel">
                <div className="md-panel-toolbar">
                    <div className="md-search-wrap">
                        <Icon d={icons.search} size={14} />
                        <input className="md-input md-search" placeholder={`Search ${activeTab}...`} value={search} onChange={e => setSearch(e.target.value)} />
                    </div>
                    {activeTab !== 'reports' && (
                        <button className="md-add-btn" onClick={() => setModal({ type: activeTab.slice(0, -1), data: null })}>
                            <Icon d={icons.plus} size={14} /> Add {activeTab.slice(0, -1).replace('-', ' ')}
                        </button>
                    )}
                </div>

                {loading ? (
                    <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8' }}>Loading operations data...</div>
                ) : (
                    <>
                        {activeTab === 'routes' && renderRoutes()}
                        {activeTab === 'trips' && renderTrips()}
                        {activeTab === 'passengers' && renderPassengers()}
                        {activeTab === 'allocation' && renderAllocations()}
                        {activeTab === 'reports' && renderReports()}
                    </>
                )}
            </div>

            {/* MODALS */}
            {modal?.type === 'route' && <RouteModal data={modal.data} onClose={() => setModal(null)} onSave={handleSave} />}

            {modal?.type === 'trip' && <TripModal data={modal.data} onClose={() => setModal(null)} onSave={handleSave} />}
            {modal?.type === 'passenger' && <PassengerModal data={modal.data} onClose={() => setModal(null)} onSave={handleSave} />}
            {modal?.type === 'allocation' && <AllocationModal onClose={() => setModal(null)} onSave={handleSave} />}

            {deleteModal && (
                <div className="md-modal-overlay" onClick={() => setDeleteModal(null)}>
                    <div className="md-modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
                        <div className="md-modal-header">
                            <h3 className="md-modal-title" style={{ color: '#ef4444' }}>Confirm Delete</h3>
                        </div>
                        <div className="md-modal-body">
                            Are you sure you want to delete this {deleteModal.type.replace('-', ' ')}? This action cannot be undone.
                        </div>
                        <div className="md-modal-footer">
                            <button className="md-cancel-btn" onClick={() => setDeleteModal(null)}>Cancel</button>
                            <button className="md-save-btn" style={{ background: '#ef4444' }} onClick={handleDelete}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
